import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_BASE_URL_WITH_API } from '../config/api';
import AdminHeader from '../components/AdminHeader';
import ImpersonationBanner from '../components/ImpersonationBanner';
import SideMenuCard, { SideMenuItem } from '../components/SideMenuCard';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { isStagingOrLocalhost } from '../utils/environment';
import { getExtensionId } from '../config/extension';
import { getChromeRuntime } from '../utils/extensionMessaging';
import { GROUPS_FEATURE_ENABLED } from '../config/features';
import { clearAllAuthStorage, getEffectiveToken, getEffectiveUser } from '../utils/authSession';
import { MENU_ICON_PATHS } from '../components/menuIcons';
import FeedbackFab from '../components/FeedbackFab';
import { getGpsBadgeStyles } from '../utils/adminBadgeStyles';
import UserStatusIcons, { StatusFilterIcon } from '../components/UserStatusIcons';
import WorkspaceSyncModal from '../components/WorkspaceSyncModal';
import { toast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';

interface DomainUser {
  id: string;
  name: string;
  email: string;
  domain?: string;
  role: string;
  isAdmin?: boolean;
  rawRole?: string;
  extensionInstalledAt?: string | null;
  extensionLastSeenAt?: string | null;
  extensionVersion?: string | null;
  trafficLiteLastActiveAt?: string | null;
  lastClickAt?: string | null;
  weeklyClicks?: number;
  coTeacherAccessLevel?: 'off' | 'basic' | 'premium';
  coTeacherAccessLevelOverride?: 'off' | 'basic' | 'premium' | null;
  assistEnabled?: boolean;
  assistEnabledOverride?: boolean | null;
  assistClicks?: number;
  assistInserts?: number;
  gpsTeaserStatus?: 'opened' | 'admin' | null;
  effectiveScale?: number | null;
  status?: string;
  groupId?: string | null;
  groupName?: string | null;
  extensionInstalled?: boolean;
  provisionedFrom?: 'manual' | 'csv' | 'google_workspace' | string | null;
  activityStatus?: 'active' | 'low_activity' | 'inactive' | 'not_yet_installed' | string | null;
  activityStatusTooltip?: string | null;
  daysSinceLastActive?: number | null;
  eligibleForInactiveCleanup?: boolean;
  eligibleForBulkRemoval?: boolean;
  workspaceWarning?: 'missing_from_source' | 'suspended' | string | null;
  workspaceWarningTooltip?: string | null;
}

interface DomainGroupOption {
  id: string;
  name: string;
}

interface AddUserFormState {
  firstName: string;
  lastName: string;
  email: string;
  role: 'teacher' | 'admin';
  groupId: string;
}

type CoTeacherAccessLevel = 'off' | 'basic' | 'premium';
type SortColumn = 'name' | 'email' | 'domain' | 'installDate' | 'version' | 'lastClick' | 'weeklyClicks' | 'assistClicks' | 'assistInserts' | 'gpsTeaserStatus' | 'coTeacherAccessLevel' | 'scale' | 'role' | 'teacherPortal' | 'group' | 'viewPolicy' | 'viewEditPolicy' | 'status';
type StatusFilter = 'all' | 'active' | 'low_activity' | 'inactive' | 'not_yet_installed' | 'workspace_warning';
const STATUS_FILTER_OPTIONS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'all', label: 'All Users' },
  { value: 'active', label: 'Active' },
  { value: 'low_activity', label: 'Low Activity (30–179 days)' },
  { value: 'inactive', label: 'Inactive (180+ days)' },
  { value: 'not_yet_installed', label: 'Not Yet Installed' },
  { value: 'workspace_warning', label: 'Workspace Warning' }
];
type GpsTeaserStatus = 'opened' | 'admin';
const formatGpsTeaserStatusLabel = (status?: string | null) => {
  if (status === 'admin') return 'Admin';
  if (status === 'opened') return 'Opened';
  return '';
};
type SortDirection = 'asc' | 'desc';
const ADMIN_USERS_FETCH_MIN_INTERVAL_MS = 2500;
const ADMIN_USERS_SORT_STORAGE_KEY = 'trafficLiteAdminUsersSort';
const GROUPS_PANEL_OPEN_QUERY_VALUE = 'open';
const UNGROUPED_PRIORITY_KEY = '__ungrouped__';
const GROUP_ICON_PATHS = {
  addNew: '/menu-icons/group-add-new.png',
  rename: '/menu-icons/group-rename.png',
  delete: '/menu-icons/group-delete.png'
};
const isGmailUser = (email?: string) => (email || '').toLowerCase().endsWith('@gmail.com');
const AdminUsersPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [users, setUsers] = useState<DomainUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [viewerIsSuperAdmin, setViewerIsSuperAdmin] = useState(false);
  const [sortColumn, setSortColumn] = useState<SortColumn>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [roleOverrides, setRoleOverrides] = useState<Record<string, string>>({});
  const [coTeacherOverrides, setCoTeacherOverrides] = useState<Record<string, CoTeacherAccessLevel>>({});
  const [saving, setSaving] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState('');
  const [domainOptions, setDomainOptions] = useState<string[]>([]);
  const [groups, setGroups] = useState<DomainGroupOption[]>([]);
  const [groupsSaving, setGroupsSaving] = useState(false);
  const [pageSavedMessage, setPageSavedMessage] = useState<string | null>(null);
  const [groupOverrides, setGroupOverrides] = useState<Record<string, string>>({});
  const [groupsPanelExpanded, setGroupsPanelExpanded] = useState(false);
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [newGroupNameInput, setNewGroupNameInput] = useState('');
  const [editingGroupId, setEditingGroupId] = useState('');
  const [editingGroupNameInput, setEditingGroupNameInput] = useState('');
  const [deletingGroupId, setDeletingGroupId] = useState('');
  const [groupsInlineError, setGroupsInlineError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [csvMenuOpen, setCsvMenuOpen] = useState(false);
  const [csvMenuHover, setCsvMenuHover] = useState<'upload' | 'template' | null>(null);
  const [statusFilterOpen, setStatusFilterOpen] = useState(false);
  const [statusFilterHover, setStatusFilterHover] = useState<StatusFilter | null>(null);
  const [workspaceModalOpen, setWorkspaceModalOpen] = useState(false);
  const [workspaceModalMode, setWorkspaceModalMode] = useState<'connect' | 'manage' | 'setup'>('connect');
  const [workspacePrivilegeFailure, setWorkspacePrivilegeFailure] = useState(false);
  const [workspaceConnection, setWorkspaceConnection] = useState<{
    status?: string;
    lastSyncAt?: string | null;
    authorizedEmail?: string;
  } | null>(null);
  const [removingUsers, setRemovingUsers] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [pendingRemoveIds, setPendingRemoveIds] = useState<string[]>([]);
  const [priorityGroupId, setPriorityGroupId] = useState('');
  const [transientChipHighlight, setTransientChipHighlight] = useState('');
  const [importingUsers, setImportingUsers] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [addingUser, setAddingUser] = useState(false);
  const [addUserModalError, setAddUserModalError] = useState<string | null>(null);
  const [addUserForm, setAddUserForm] = useState<AddUserFormState>({
    firstName: '',
    lastName: '',
    email: '',
    role: 'teacher',
    groupId: ''
  });
  const [importSummary, setImportSummary] = useState<{
    createdCount: number;
    updatedCount: number;
    failedCount: number;
    failed: Array<{ row: number; email: string; error: string }>;
  } | null>(null);
  const [hoveredRowId, setHoveredRowId] = useState('');
  const usePersistentSidebar = isStagingOrLocalhost();
  const showVersionColumn = viewerIsSuperAdmin;
  const usersFetchInFlightRef = useRef<Promise<void> | null>(null);
  const lastUsersFetchAtRef = useRef(0);
  const appliedSuperDefaultSortRef = useRef(false);
  const chipHighlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const csvFileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    localStorage.setItem(
      ADMIN_USERS_SORT_STORAGE_KEY,
      JSON.stringify({ sortColumn, sortDirection })
    );
  }, [sortColumn, sortDirection]);

  useEffect(() => {
    if (viewerIsSuperAdmin || !GROUPS_FEATURE_ENABLED) return;
    const groupsParam = String(searchParams.get('groups') || '').toLowerCase();
    if (groupsParam === GROUPS_PANEL_OPEN_QUERY_VALUE) {
      navigate('/admin/groups');
      return;
    }
    const groupIdParam = String(searchParams.get('groupId') || '').trim();
    if (groupIdParam) {
      setPriorityGroupId(groupIdParam);
      setSortColumn('group');
      setSortDirection('asc');
    }
    const searchQueryParam = String(searchParams.get('q') || '').trim();
    if (searchQueryParam) {
      setSearchQuery(searchQueryParam);
    }
  }, [searchParams, viewerIsSuperAdmin, navigate]);

  useEffect(() => {
    if (!priorityGroupId) return;
    const stillExists = groups.some((group) => group.id === priorityGroupId);
    if (!stillExists && priorityGroupId !== UNGROUPED_PRIORITY_KEY) {
      setPriorityGroupId('');
    }
  }, [groups, priorityGroupId]);

  useEffect(() => () => {
    if (chipHighlightTimeoutRef.current) {
      clearTimeout(chipHighlightTimeoutRef.current);
    }
  }, []);

  const fetchUsers = useCallback(async (silent = false) => {
    const now = Date.now();
    if (usersFetchInFlightRef.current) {
      return usersFetchInFlightRef.current;
    }
    if (now - lastUsersFetchAtRef.current < ADMIN_USERS_FETCH_MIN_INTERVAL_MS) {
      return;
    }
    lastUsersFetchAtRef.current = now;

    const run = (async () => {
      if (!silent) setLoading(true);
      setError(null);
      try {
        const token = getEffectiveToken();
        if (!token) {
          setError('Not authenticated. Please sign in again.');
          if (!silent) setLoading(false);
          return;
        }

        const baseUrl = API_BASE_URL_WITH_API.endsWith('/api')
          ? API_BASE_URL_WITH_API
          : `${API_BASE_URL_WITH_API}/api`;
        const params = new URLSearchParams();
        if (selectedDomain.trim()) {
          params.append('domain', selectedDomain.trim());
        }
        const response = await fetch(`${baseUrl}/admin/users${params.toString() ? `?${params.toString()}` : ''}`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          cache: 'no-store'
        });

        if (!response.ok) {
          if (response.status === 403) {
            throw new Error('Admin access required');
          }
          throw new Error(`Failed to fetch users: ${response.statusText}`);
        }

        const data = await response.json();
        if (data.success && data.users) {
          setUsers(data.users);
          setViewerIsSuperAdmin(data.viewerIsSuperAdmin === true);
          if (Array.isArray(data.groups)) {
            setGroups(data.groups);
          }
          if (Array.isArray(data.domains)) {
            setDomainOptions(data.domains);
          }
          setSelectedUserIds([]);
        } else {
          setError('Invalid response from server');
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to fetch users';
        setError(message);
      } finally {
        if (!silent) setLoading(false);
      }
    })();

    usersFetchInFlightRef.current = run;
    try {
      await run;
    } finally {
      if (usersFetchInFlightRef.current === run) {
        usersFetchInFlightRef.current = null;
      }
    }
  }, [selectedDomain]);

  useEffect(() => {
    document.title = 'Users - Traffic Lite Admin';
    let initialViewerIsSuperAdmin = false;
    const groupIdParam = String(searchParams.get('groupId') || '').trim();
    const searchQueryParam = String(searchParams.get('q') || '').trim();
    try {
      const user = getEffectiveUser();
      if (user) {
        if (user?.isSuperAdmin) {
          initialViewerIsSuperAdmin = true;
          setViewerIsSuperAdmin(true);
        }
      }
    } catch (parseError) {
      console.warn('Could not parse trafficLiteUser from localStorage:', parseError);
    }
    try {
      if (initialViewerIsSuperAdmin) {
        setSortColumn('installDate');
        setSortDirection('desc');
      } else {
        if (searchQueryParam) {
          setSearchQuery(searchQueryParam);
        }
        if (groupIdParam) {
          setPriorityGroupId(groupIdParam);
          setSortColumn('group');
          setSortDirection('asc');
        } else {
          const rawSort = localStorage.getItem(ADMIN_USERS_SORT_STORAGE_KEY);
          if (rawSort) {
            const parsedSort = JSON.parse(rawSort) as { sortColumn?: SortColumn; sortDirection?: SortDirection };
            const allowedColumns: SortColumn[] = ['name', 'email', 'domain', 'installDate', 'version', 'lastClick', 'weeklyClicks', 'assistClicks', 'assistInserts', 'gpsTeaserStatus', 'coTeacherAccessLevel', 'scale', 'role', 'teacherPortal', 'group', 'viewPolicy', 'viewEditPolicy', 'status'];
            const allowedDirections: SortDirection[] = ['asc', 'desc'];
            if (parsedSort?.sortColumn && allowedColumns.includes(parsedSort.sortColumn)) {
              setSortColumn(parsedSort.sortColumn);
            }
            if (parsedSort?.sortDirection && allowedDirections.includes(parsedSort.sortDirection)) {
              setSortDirection(parsedSort.sortDirection);
            }
          }
        }
      }
    } catch (sortParseError) {
      console.warn('Could not parse saved users sort preferences:', sortParseError);
    }
    void fetchUsers();
  }, [fetchUsers, searchParams]);

  useEffect(() => {
    document.title = 'Users - Traffic Lite Admin';
  }, [viewerIsSuperAdmin]);

  useEffect(() => {
    if (!viewerIsSuperAdmin) return;
    if (!appliedSuperDefaultSortRef.current) {
      setSortColumn('installDate');
      setSortDirection('desc');
      appliedSuperDefaultSortRef.current = true;
    }
    setRoleOverrides({});
    setCoTeacherOverrides({});
    lastUsersFetchAtRef.current = 0;
    void fetchUsers(true);
  }, [selectedDomain, viewerIsSuperAdmin, fetchUsers]);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const renderSortIndicator = (column: SortColumn) => {
    if (sortColumn !== column) return null;
    return (
      <span className="traffic-sort-indicator" style={{ marginLeft: '6px', display: 'inline-flex', alignItems: 'center' }}>
        <img
          src={sortDirection === 'asc' ? MENU_ICON_PATHS.upArrow : MENU_ICON_PATHS.downArrow}
          alt=""
          style={{ width: '11px', height: '11px', objectFit: 'contain' }}
        />
      </span>
    );
  };

  const getEffectiveRole = (u: DomainUser) => {
    if (roleOverrides[u.id] !== undefined) return roleOverrides[u.id];
    return u.isAdmin ? 'admin' : 'teacher';
  };

  const hasChanges = Object.keys(roleOverrides).length > 0
    || Object.keys(coTeacherOverrides).length > 0;

  const handleRoleChange = (userId: string, newRole: string) => {
    const user = users.find((u) => u.id === userId);
    if (!user) return;
    const currentEffective = user.isAdmin ? 'admin' : 'teacher';
    if (newRole === currentEffective) {
      setRoleOverrides((prev) => {
        const next = { ...prev };
        delete next[userId];
        return next;
      });
    } else {
      setRoleOverrides((prev) => ({ ...prev, [userId]: newRole }));
    }
  };

  const normalizeCoTeacherLevel = (value: string): CoTeacherAccessLevel => {
    const normalized = String(value || '').toLowerCase();
    return (normalized === 'off' || normalized === 'basic' || normalized === 'premium')
      ? normalized
      : 'off';
  };

  const handleCoTeacherAccessChange = (userId: string, nextValue: CoTeacherAccessLevel) => {
    const user = users.find((u) => u.id === userId);
    if (!user) return;
    const initial = normalizeCoTeacherLevel(user.coTeacherAccessLevel || 'off');
    if (nextValue === initial) {
      setCoTeacherOverrides((prev) => {
        const next = { ...prev };
        delete next[userId];
        return next;
      });
      return;
    }
    setCoTeacherOverrides((prev) => ({ ...prev, [userId]: nextValue }));
  };

  const getEffectiveCoTeacherAccess = (u: DomainUser): CoTeacherAccessLevel => {
    if (coTeacherOverrides[u.id] !== undefined) return coTeacherOverrides[u.id];
    return normalizeCoTeacherLevel(u.coTeacherAccessLevel || 'off');
  };
  const getEffectiveGroupId = (u: DomainUser) => {
    if (groupOverrides[u.id] !== undefined) return groupOverrides[u.id];
    return u.groupId || '';
  };
  const hasGroupChanges = Object.keys(groupOverrides).length > 0;

  const handleSaveAll = async () => {
    if (!hasChanges || saving) return;
    setSaving(true);
    setError(null);
    const token = getEffectiveToken();
    if (!token) {
      setError('Not authenticated');
      setSaving(false);
      return;
    }
    const baseUrl = API_BASE_URL_WITH_API.endsWith('/api')
      ? API_BASE_URL_WITH_API
      : `${API_BASE_URL_WITH_API}/api`;
    try {
      const roleUpdates = Object.entries(roleOverrides).map(([userId, role]) => {
        const user = users.find((u) => u.id === userId);
        return user ? { user, role } : null;
      }).filter(Boolean) as { user: DomainUser; role: string }[];
      const coTeacherUpdates = Object.entries(coTeacherOverrides).map(([userId, coTeacherAccessLevelOverride]) => {
        const user = users.find((u) => u.id === userId);
        return user ? { user, coTeacherAccessLevelOverride } : null;
      }).filter(Boolean) as { user: DomainUser; coTeacherAccessLevelOverride: CoTeacherAccessLevel }[];
      const updateMap = new Map<string, { user: DomainUser; role?: string; coTeacherAccessLevelOverride?: CoTeacherAccessLevel }>();
      roleUpdates.forEach(({ user, role }) => {
        const existing = updateMap.get(user.id) || { user };
        existing.role = role;
        updateMap.set(user.id, existing);
      });
      coTeacherUpdates.forEach(({ user, coTeacherAccessLevelOverride }) => {
        const existing = updateMap.get(user.id) || { user };
        existing.coTeacherAccessLevelOverride = coTeacherAccessLevelOverride;
        updateMap.set(user.id, existing);
      });
      for (const { user, role, coTeacherAccessLevelOverride } of updateMap.values()) {
        const response = await fetch(`${baseUrl}/admin/users/${user.id}`, {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ...(role ? { role } : {}),
            ...(coTeacherAccessLevelOverride !== undefined ? { coTeacherAccessLevelOverride } : {})
          })
        });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error || `Failed to update ${user.email}`);
        }
      }
      setRoleOverrides({});
      setCoTeacherOverrides({});
      await fetchUsers(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const createGroup = async (name: string) => {
    const token = getEffectiveToken();
    if (!token) throw new Error('Not authenticated');
    const baseUrl = API_BASE_URL_WITH_API.endsWith('/api')
      ? API_BASE_URL_WITH_API
      : `${API_BASE_URL_WITH_API}/api`;
    const response = await fetch(`${baseUrl}/admin/groups`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ name })
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to create group');
    }
    return data.group as DomainGroupOption;
  };

  const handleAssignGroup = async (userId: string, nextGroupId: string) => {
    const user = users.find((u) => u.id === userId);
    if (!user) return;
    const currentGroupId = user.groupId || '';
    if ((nextGroupId || '') === currentGroupId) {
      setGroupOverrides((prev) => {
        const next = { ...prev };
        delete next[userId];
        return next;
      });
      return;
    }
    setGroupOverrides((prev) => ({ ...prev, [userId]: nextGroupId || '' }));
  };

  const handleSaveGroupChanges = async () => {
    const token = getEffectiveToken();
    if (!token) {
      setError('Not authenticated');
      return;
    }
    setGroupsSaving(true);
    setError(null);
    try {
      const baseUrl = API_BASE_URL_WITH_API.endsWith('/api')
        ? API_BASE_URL_WITH_API
        : `${API_BASE_URL_WITH_API}/api`;
      for (const [userId, nextGroupId] of Object.entries(groupOverrides)) {
        const response = await fetch(`${baseUrl}/admin/users/${userId}`, {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ groupId: nextGroupId || null })
        });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error || 'Failed to assign group');
        }
      }
      setGroupOverrides({});
      await fetchUsers(true);
      setPageSavedMessage('Users page saved.');
      setTimeout(() => setPageSavedMessage(null), 2500);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to assign group');
    } finally {
      setGroupsSaving(false);
    }
  };

  const handleGroupSelectChange = async (userId: string, value: string) => {
    await handleAssignGroup(userId, value);
  };

  const normalizeGroupName = (value: string) => value.trim().replace(/\s+/g, ' ');

  const hasGroupNameConflict = (candidateName: string, excludeGroupId?: string) => {
    const normalizedCandidate = normalizeGroupName(candidateName).toLowerCase();
    return groups.some((group) => {
      if (excludeGroupId && group.id === excludeGroupId) return false;
      return normalizeGroupName(group.name).toLowerCase() === normalizedCandidate;
    });
  };

  const handleCreateGroupInline = async () => {
    const nextName = normalizeGroupName(newGroupNameInput);
    if (!nextName) {
      setGroupsInlineError('Please enter a group name.');
      return;
    }
    if (hasGroupNameConflict(nextName)) {
      setGroupsInlineError('A group with this name already exists.');
      return;
    }
    setGroupsSaving(true);
    setError(null);
    setGroupsInlineError(null);
    const token = getEffectiveToken();
    if (!token) {
      setGroupsInlineError('Not authenticated.');
      setGroupsSaving(false);
      return;
    }
    try {
      const createdGroup = await createGroup(nextName);
      setGroups((prev) => [...prev, createdGroup]);
      setCreatingGroup(false);
      setNewGroupNameInput('');
      lastUsersFetchAtRef.current = 0;
      await fetchUsers(true);
    } catch (err: unknown) {
      setGroupsInlineError(err instanceof Error ? err.message : 'Failed to create group.');
    } finally {
      setGroupsSaving(false);
    }
  };

  const handleStartRenameGroup = (group: DomainGroupOption) => {
    setGroupsInlineError(null);
    setDeletingGroupId('');
    setEditingGroupId(group.id);
    setEditingGroupNameInput(group.name);
  };

  const handleSaveRenameGroup = async (groupId: string) => {
    const nextName = normalizeGroupName(editingGroupNameInput);
    if (!nextName) {
      setGroupsInlineError('Group name cannot be empty.');
      return;
    }
    if (hasGroupNameConflict(nextName, groupId)) {
      setGroupsInlineError('A group with this name already exists.');
      return;
    }

    setGroupsSaving(true);
    setError(null);
    setGroupsInlineError(null);
    const token = getEffectiveToken();
    if (!token) {
      setGroupsInlineError('Not authenticated.');
      setGroupsSaving(false);
      return;
    }
    try {
      const baseUrl = API_BASE_URL_WITH_API.endsWith('/api')
        ? API_BASE_URL_WITH_API
        : `${API_BASE_URL_WITH_API}/api`;
      const response = await fetch(`${baseUrl}/admin/groups/${groupId}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: nextName })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to rename group');
      }

      setGroups((prev) => prev.map((group) => (
        group.id === groupId ? { ...group, name: nextName } : group
      )));
      setEditingGroupId('');
      setEditingGroupNameInput('');
      lastUsersFetchAtRef.current = 0;
      await fetchUsers(true);
    } catch (err: unknown) {
      setGroupsInlineError(err instanceof Error ? err.message : 'Failed to rename group.');
    } finally {
      setGroupsSaving(false);
    }
  };

  const handleDeleteGroupInline = async (groupId: string) => {
    const token = getEffectiveToken();
    if (!token) {
      setGroupsInlineError('Not authenticated.');
      return;
    }
    setGroupsSaving(true);
    setError(null);
    setGroupsInlineError(null);
    try {
      const baseUrl = API_BASE_URL_WITH_API.endsWith('/api')
        ? API_BASE_URL_WITH_API
        : `${API_BASE_URL_WITH_API}/api`;
      const response = await fetch(`${baseUrl}/admin/groups/${groupId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete group');
      }

      setGroups((prev) => prev.filter((group) => group.id !== groupId));
      setGroupOverrides((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((userId) => {
          if (next[userId] === groupId) next[userId] = '';
        });
        return next;
      });
      setUsers((prev) => prev.map((user) => (
        user.groupId === groupId ? { ...user, groupId: null, groupName: null } : user
      )));
      setDeletingGroupId('');
      if (editingGroupId === groupId) {
        setEditingGroupId('');
        setEditingGroupNameInput('');
      }
      lastUsersFetchAtRef.current = 0;
      await fetchUsers(true);
    } catch (err: unknown) {
      setGroupsInlineError(err instanceof Error ? err.message : 'Failed to delete group.');
    } finally {
      setGroupsSaving(false);
    }
  };

  const getSortValue = (u: DomainUser, col: SortColumn) => {
    if (col === 'name') return (u.name || '').toLowerCase();
    if (col === 'email') return (u.email || '').toLowerCase();
    if (col === 'domain') return (u.domain || '').toLowerCase();
    if (col === 'installDate') {
      if (!u.extensionInstalledAt) return Number.NEGATIVE_INFINITY;
      const installMs = new Date(u.extensionInstalledAt).getTime();
      return Number.isFinite(installMs) ? installMs : Number.NEGATIVE_INFINITY;
    }
    if (col === 'version') return String(u.extensionVersion || '').toLowerCase();
    if (col === 'lastClick') {
      const clickRaw = u.lastClickAt || u.trafficLiteLastActiveAt;
      if (!clickRaw) return Number.NEGATIVE_INFINITY;
      const clickMs = new Date(clickRaw).getTime();
      return Number.isFinite(clickMs) ? clickMs : Number.NEGATIVE_INFINITY;
    }
    if (col === 'status') {
      const rank: Record<string, number> = {
        active: 4,
        low_activity: 3,
        inactive: 2,
        not_yet_installed: 1
      };
      return (rank[String(u.activityStatus || '')] || 0) + (u.workspaceWarning ? 0.5 : 0);
    }
    if (col === 'weeklyClicks') return Number(u.weeklyClicks || 0);
    if (col === 'assistClicks') return Number(u.assistClicks || 0);
    if (col === 'assistInserts') return Number(u.assistInserts || 0);
    if (col === 'gpsTeaserStatus') {
      const rank = { opened: 1, admin: 2 } as const;
      const status = String(u.gpsTeaserStatus || '').trim().toLowerCase() as GpsTeaserStatus;
      return rank[status] || 0;
    }
    if (col === 'coTeacherAccessLevel') {
      const rank = { off: 0, basic: 1, premium: 2 } as const;
      return rank[getEffectiveCoTeacherAccess(u)];
    }
    if (col === 'scale') return Number(u.effectiveScale || 0);
    if (col === 'teacherPortal') {
      if (viewerIsSuperAdmin) {
        const targetRole = String(u.rawRole || u.role || '').toLowerCase();
        return targetRole === 'super_admin' ? 'zzz-unavailable' : 'log in as user';
      }
      const isTeacher = (u.rawRole || u.role) === 'teacher' && !u.isAdmin;
      return isTeacher ? 'log in as user' : 'zzz-unavailable';
    }
    if (col === 'viewPolicy') {
      const isTeacher = (u.rawRole || u.role) === 'teacher' && !u.isAdmin;
      return isTeacher ? 'log in as user' : 'zzz-unavailable';
    }
    if (col === 'viewEditPolicy') {
      return 'view & edit policy';
    }
    if (col === 'group') {
      const isTeacher = (u.rawRole || u.role) === 'teacher' && !u.isAdmin;
      if (!isTeacher) return '';
      const groupId = getEffectiveGroupId(u);
      const groupName = groups.find((group) => group.id === groupId)?.name || 'No Group';
      return groupName.toLowerCase();
    }
    return (u.role || '').toLowerCase();
  };

  const getRoleBadgeStyles = (role: string) => {
    if (role === 'super_admin') {
      return { backgroundColor: '#fff3e0', color: '#e65100', borderColor: '#ffcc80' };
    }
    if (role === 'admin') {
      return { backgroundColor: '#e8eaf6', color: '#3949ab', borderColor: '#c5cae9' };
    }
    if (role === 'teacher') {
      return { backgroundColor: '#e8f5e9', color: '#2e7d32', borderColor: '#a5d6a7' };
    }
    return { backgroundColor: '#fffde7', color: '#8d6e00', borderColor: '#ffe082' };
  };

  const formatInstallDate = (isoDate?: string | null) => {
    if (!isoDate) return '-';
    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) return '-';
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    }).format(date);
  };

  const formatLastClick = (isoDate?: string | null) => {
    if (!isoDate) return '';
    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    }).format(date);
  };

  const formatScaleLabel = (scale?: number | null) => {
    if (scale === 2 || scale === 3 || scale === 4 || scale === 5) return `${scale}-color`;
    return '';
  };

  const isExtensionInstalledUser = (user: DomainUser) => Boolean(
    user.extensionInstalled
    || user.extensionInstalledAt
    || user.extensionLastSeenAt
    || user.trafficLiteLastActiveAt
  );

  const handleStartUserImpersonation = async (targetUser: DomainUser) => {
    const targetRole = String(targetUser.rawRole || targetUser.role || '').toLowerCase();
    if (!viewerIsSuperAdmin && (targetRole !== 'teacher' || targetUser.isAdmin)) return;
    if (viewerIsSuperAdmin && targetRole === 'super_admin') return;
    setError(null);
    try {
      const token = getEffectiveToken();
      if (!token) {
        setError('Not authenticated. Please sign in again.');
        return;
      }
      const baseUrl = API_BASE_URL_WITH_API.endsWith('/api')
        ? API_BASE_URL_WITH_API
        : `${API_BASE_URL_WITH_API}/api`;
      const response = await fetch(`${baseUrl}/admin/impersonation/start-user`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userId: targetUser.id })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to start user impersonation');
      }

      const payload = window.btoa(JSON.stringify({
        token: data.token,
        user: data.user,
        domain: data.domain,
        targetEmail: data.targetEmail,
        redirectPath: data.redirectPath,
        returnPath: data.returnPath,
        returnLabel: data.returnLabel,
        actorRole: data.actorRole
      }));
      const url = `${window.location.origin}${window.location.pathname}#/admin/impersonate?payload=${encodeURIComponent(payload)}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to start user impersonation');
    }
  };

  const handleOpenViewEditPolicy = (targetUser: DomainUser) => {
    if (viewerIsSuperAdmin) return;
    const groupId = String(targetUser.groupId || '').trim();
    const policyPath = groupId
      ? `#/admin/settings?groupId=${encodeURIComponent(groupId)}`
      : '#/admin/settings';
    const url = `${window.location.origin}${window.location.pathname}${policyPath}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const renderPortalIconButton = ({
    label,
    onClick,
    iconPath,
    disabled = false
  }: {
    label: string;
    onClick: () => void;
    iconPath: string;
    disabled?: boolean;
  }) => (
    <button
      type="button"
      onClick={() => {
        if (disabled) return;
        onClick();
      }}
      disabled={disabled}
      title={label}
      aria-label={label}
      className="icon-action-btn"
    >
      <span
        aria-hidden="true"
        className="icon-action-glyph"
        style={{
          WebkitMaskImage: `url(${iconPath})`,
          maskImage: `url(${iconPath})`
        }}
      />
    </button>
  );

  const filteredUsers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const statusMatches = (user: DomainUser) => {
      if (statusFilter === 'all') return true;
      if (statusFilter === 'workspace_warning') return Boolean(user.workspaceWarning);
      return user.activityStatus === statusFilter;
    };
    return users.filter((user) => {
      if (!statusMatches(user)) return false;
      if (!query) return true;
      const roleText = (user.role || '').toLowerCase();
      const domainText = (user.domain || '').toLowerCase();
      const groupId = getEffectiveGroupId(user);
      const isTeacher = (user.rawRole || user.role) === 'teacher' && !user.isAdmin;
      const groupName = isTeacher
        ? (groups.find((group) => group.id === groupId)?.name || 'No Group')
        : '';
      const statusText = [
        user.activityStatus || '',
        user.activityStatusTooltip || '',
        user.workspaceWarning || '',
        user.workspaceWarningTooltip || ''
      ].join(' ').toLowerCase();
      const superAdminSearchFields = viewerIsSuperAdmin
        ? [
            getEffectiveRole(user),
            formatInstallDate(user.extensionInstalledAt),
            user.extensionInstalledAt || '',
            formatLastClick(user.lastClickAt || user.trafficLiteLastActiveAt),
            user.lastClickAt || user.trafficLiteLastActiveAt || '',
            String(Number(user.weeklyClicks || 0)),
            String(Math.round(Number(user.weeklyClicks || 0))),
            String(Number(user.assistClicks || 0)),
            String(Number(user.assistInserts || 0)),
            formatGpsTeaserStatusLabel(user.gpsTeaserStatus),
            `co-teacher ${getEffectiveCoTeacherAccess(user)}`,
            formatScaleLabel(user.effectiveScale),
            String(user.effectiveScale ?? '')
          ]
        : [];
      const superAdminSearchText = superAdminSearchFields
        .map((value) => String(value || '').toLowerCase())
        .join(' ');
      return (
        (user.name || '').toLowerCase().includes(query)
        || (user.email || '').toLowerCase().includes(query)
        || domainText.includes(query)
        || roleText.includes(query)
        || groupName.toLowerCase().includes(query)
        || statusText.includes(query)
        || superAdminSearchText.includes(query)
      );
    });
  }, [users, searchQuery, statusFilter, groups, groupOverrides, coTeacherOverrides, roleOverrides, viewerIsSuperAdmin]);

  const sortedGroups = useMemo(
    () => [...groups].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })),
    [groups]
  );

  const groupAssignmentCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    users.forEach((user) => {
      const isTeacher = (user.rawRole || user.role) === 'teacher' && !user.isAdmin;
      if (!isTeacher) return;
      const groupId = getEffectiveGroupId(user);
      if (!groupId) return;
      counts[groupId] = (counts[groupId] || 0) + 1;
    });
    return counts;
  }, [users, groupOverrides]);

  const ungroupedUserCount = useMemo(() => {
    let count = 0;
    users.forEach((user) => {
      const isTeacher = (user.rawRole || user.role) === 'teacher' && !user.isAdmin;
      if (!isTeacher) return;
      if (!getEffectiveGroupId(user)) {
        count += 1;
      }
    });
    return count;
  }, [users, groupOverrides]);

  const handlePrioritizeGroup = (groupId: string) => {
    setPriorityGroupId(groupId);
    setSortColumn('group');
    setSortDirection('asc');
    setTransientChipHighlight(groupId);
    if (chipHighlightTimeoutRef.current) {
      clearTimeout(chipHighlightTimeoutRef.current);
    }
    chipHighlightTimeoutRef.current = setTimeout(() => {
      setTransientChipHighlight('');
    }, 850);
  };

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    if (viewerIsSuperAdmin) {
      const aPreProvisioned = !isExtensionInstalledUser(a);
      const bPreProvisioned = !isExtensionInstalledUser(b);
      if (aPreProvisioned !== bPreProvisioned) {
        return aPreProvisioned ? 1 : -1;
      }
    }
    if (!viewerIsSuperAdmin) {
      const aInstalled = isExtensionInstalledUser(a);
      const bInstalled = isExtensionInstalledUser(b);
      if (aInstalled !== bInstalled) {
        return aInstalled ? -1 : 1;
      }
      if (!aInstalled && !bInstalled) {
        const nameCompare = (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
        if (nameCompare !== 0) {
          return nameCompare;
        }
      }
    }

    if (priorityGroupId) {
      const aGroupId = getEffectiveGroupId(a);
      const bGroupId = getEffectiveGroupId(b);
      const aIsTeacher = (a.rawRole || a.role) === 'teacher' && !a.isAdmin;
      const bIsTeacher = (b.rawRole || b.role) === 'teacher' && !b.isAdmin;
      const aInPriorityGroup = priorityGroupId === UNGROUPED_PRIORITY_KEY
        ? aIsTeacher && !aGroupId
        : aIsTeacher && aGroupId === priorityGroupId;
      const bInPriorityGroup = priorityGroupId === UNGROUPED_PRIORITY_KEY
        ? bIsTeacher && !bGroupId
        : bIsTeacher && bGroupId === priorityGroupId;
      if (aInPriorityGroup !== bInPriorityGroup) {
        return aInPriorityGroup ? -1 : 1;
      }
    }
    const aVal = getSortValue(a, sortColumn);
    const bVal = getSortValue(b, sortColumn);
    const compare = typeof aVal === 'number' && typeof bVal === 'number'
      ? aVal - bVal
      : String(aVal).localeCompare(String(bVal));
    return sortDirection === 'asc' ? compare : -compare;
  });

  const escapeCsvValue = (value: string) => `"${(value || '').replace(/"/g, '""')}"`;

  const handleExportCsv = () => {
    if (!viewerIsSuperAdmin) return;

    const headers = ['Name (Last, First)', 'Email', 'Domain', 'Install Date', ...(showVersionColumn ? ['Version'] : []), 'Last Active', 'Weekly Clicks', 'GPS', 'GPS Clicks', 'Suggestion Inserts', 'GPS Teaser', 'Scale', 'Role'];
    const rows = sortedUsers.map((u) => [
      u.name || '-',
      u.email || '-',
      u.domain || '-',
      formatInstallDate(u.extensionInstalledAt),
      ...(showVersionColumn ? [u.extensionVersion || '-'] : []),
      formatLastClick(u.lastClickAt || u.trafficLiteLastActiveAt),
      String(Math.round(Number(u.weeklyClicks || 0))),
      getEffectiveCoTeacherAccess(u),
      String(Number(u.assistClicks || 0)),
      String(Number(u.assistInserts || 0)),
      formatGpsTeaserStatusLabel(u.gpsTeaserStatus),
      formatScaleLabel(u.effectiveScale),
      u.role || 'teacher'
    ]);

    const csv = [
      headers.map(escapeCsvValue).join(','),
      ...rows.map((row) => row.map((cell) => escapeCsvValue(String(cell ?? ''))).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    link.href = url;
    link.download = `traffic-lite-users-${timestamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const parseCsvLine = (line: string) => {
    const values: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i += 1) {
      const char = line[i];
      if (char === '"') {
        const next = line[i + 1];
        if (inQuotes && next === '"') {
          current += '"';
          i += 1;
          continue;
        }
        inQuotes = !inQuotes;
        continue;
      }
      if (char === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
        continue;
      }
      current += char;
    }
    values.push(current.trim());
    return values;
  };

  const resetAddUserForm = useCallback(() => {
    setAddUserForm({
      firstName: '',
      lastName: '',
      email: '',
      role: 'teacher',
      groupId: ''
    });
  }, []);

  const handleOpenAddUserModal = () => {
    resetAddUserForm();
    setAddUserModalError(null);
    setShowAddUserModal(true);
  };

  const handleCloseAddUserModal = () => {
    if (addingUser) return;
    setShowAddUserModal(false);
    setAddUserModalError(null);
    resetAddUserForm();
  };

  const handleAddUserSubmit = async () => {
    if (addingUser) return;
    const email = addUserForm.email.trim();
    if (!email) {
      setAddUserModalError('Email is required.');
      return;
    }
    if (viewerIsSuperAdmin && !addUserForm.role) {
      setAddUserModalError('Role is required for Super Admin add user.');
      return;
    }

    setAddingUser(true);
    setAddUserModalError(null);
    setError(null);
    try {
      const token = getEffectiveToken();
      if (!token) {
        throw new Error('Not authenticated.');
      }
      const baseUrl = API_BASE_URL_WITH_API.endsWith('/api')
        ? API_BASE_URL_WITH_API
        : `${API_BASE_URL_WITH_API}/api`;
      const payload: Record<string, string> = {
        firstName: addUserForm.firstName.trim(),
        lastName: addUserForm.lastName.trim(),
        email
      };
      if (viewerIsSuperAdmin) {
        payload.role = addUserForm.role;
      } else if (GROUPS_FEATURE_ENABLED) {
        payload.groupId = addUserForm.groupId || '';
      }
      const response = await fetch(`${baseUrl}/admin/users`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to add user.');
      }
      setShowAddUserModal(false);
      setAddUserModalError(null);
      resetAddUserForm();
      await fetchUsers(true);
      setPageSavedMessage('User added.');
      setTimeout(() => setPageSavedMessage(null), 2500);
    } catch (err: unknown) {
      setAddUserModalError(err instanceof Error ? err.message : 'Failed to add user.');
    } finally {
      setAddingUser(false);
    }
  };

  const handleDownloadCsvTemplate = () => {
    const csv = 'Last Name,First Name,Email,Group\n';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'traffic-lite-users-template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleOpenImportUsers = () => {
    if (viewerIsSuperAdmin) return;
    csvFileInputRef.current?.click();
  };

  const handleImportUsersFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || viewerIsSuperAdmin) return;
    setImportingUsers(true);
    setError(null);
    try {
      const csvText = await file.text();
      const lines = csvText
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);
      if (lines.length < 2) {
        throw new Error('CSV file is empty.');
      }
      const [headerLine, ...dataLines] = lines;
      const headers = parseCsvLine(headerLine).map((h) => h.toLowerCase());
      const lastNameIdx = headers.indexOf('last name');
      const firstNameIdx = headers.indexOf('first name');
      const emailIdx = headers.indexOf('email');
      const groupIdx = headers.indexOf('group');
      if (lastNameIdx < 0 || firstNameIdx < 0 || emailIdx < 0 || groupIdx < 0) {
        throw new Error('CSV must include headers: Last Name, First Name, Email, Group.');
      }
      const rows = dataLines.map((line) => {
        const cols = parseCsvLine(line);
        return {
          lastName: cols[lastNameIdx] || '',
          firstName: cols[firstNameIdx] || '',
          email: cols[emailIdx] || '',
          group: cols[groupIdx] || ''
        };
      }).filter((row) => row.email.trim() || row.firstName.trim() || row.lastName.trim() || row.group.trim());

      if (rows.length === 0) {
        throw new Error('No user rows found in CSV.');
      }

      const token = getEffectiveToken();
      if (!token) {
        throw new Error('Not authenticated.');
      }
      const baseUrl = API_BASE_URL_WITH_API.endsWith('/api')
        ? API_BASE_URL_WITH_API
        : `${API_BASE_URL_WITH_API}/api`;
      const response = await fetch(`${baseUrl}/admin/users/import`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ rows })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to import users.');
      }
      setImportSummary({
        createdCount: Number(data.createdCount || 0),
        updatedCount: Number(data.updatedCount || 0),
        failedCount: Number(data.failedCount || 0),
        failed: Array.isArray(data.failed) ? data.failed : []
      });
      await fetchUsers(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to import users.');
    } finally {
      setImportingUsers(false);
    }
  };

  const handleDeleteUser = async (user: DomainUser) => {
    if (viewerIsSuperAdmin || !user?.id) return;
    if (user.extensionInstalled || user.extensionInstalledAt || user.extensionLastSeenAt || user.trafficLiteLastActiveAt) return;
    setPendingRemoveIds([user.id]);
    setRemoveDialogOpen(true);
  };

  const fetchWorkspaceStatus = useCallback(async () => {
    if (viewerIsSuperAdmin) return;
    try {
      const token = getEffectiveToken();
      if (!token) return;
      const baseUrl = API_BASE_URL_WITH_API.endsWith('/api')
        ? API_BASE_URL_WITH_API
        : `${API_BASE_URL_WITH_API}/api`;
      const response = await fetch(`${baseUrl}/admin/workspace-sync/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setWorkspaceConnection(data.connection || null);
      }
    } catch (_) {}
  }, [viewerIsSuperAdmin]);

  useEffect(() => {
    if (!viewerIsSuperAdmin) {
      void fetchWorkspaceStatus();
    }
  }, [viewerIsSuperAdmin, fetchWorkspaceStatus]);

  useEffect(() => {
    if (viewerIsSuperAdmin) return;
    const workspaceSync = String(searchParams.get('workspaceSync') || '').trim();
    if (!workspaceSync) return;

    const next = new URLSearchParams(searchParams);
    next.delete('workspaceSync');
    next.delete('reason');
    const query = next.toString();
    const clearWorkspaceSyncQuery = () => {
      navigate(query ? `/admin/users?${query}` : '/admin/users', { replace: true });
    };

    if (workspaceSync === 'connected') {
      // Directory OAuth succeeded — continue setup, but do not sync yet.
      setWorkspacePrivilegeFailure(false);
      setWorkspaceModalMode('setup');
      setWorkspaceModalOpen(true);
      void fetchWorkspaceStatus();
      clearWorkspaceSyncQuery();
      return;
    }

    if (workspaceSync === 'error') {
      const reason = String(searchParams.get('reason') || '').trim();
      if (reason === 'insufficient_google_privileges') {
        setWorkspacePrivilegeFailure(true);
        setWorkspaceModalOpen(true);
      } else if (reason === 'domain_mismatch') {
        setError('The Google account must belong to the same domain as this Traffic Lite Admin.');
      } else {
        setError('Google Workspace connection did not complete. Try again with a Google account that has Directory privileges.');
      }
      clearWorkspaceSyncQuery();
    }
  }, [searchParams, viewerIsSuperAdmin, navigate, fetchWorkspaceStatus]);

  const selectableFilteredUsers = useMemo(
    () => filteredUsers.filter((user) => user.eligibleForBulkRemoval !== false && !user.isAdmin && String(user.role || '').toLowerCase() !== 'super_admin'),
    [filteredUsers]
  );

  const selectedVisibleCount = selectableFilteredUsers.filter((user) => selectedUserIds.includes(user.id)).length;
  const allVisibleSelected = selectableFilteredUsers.length > 0 && selectedVisibleCount === selectableFilteredUsers.length;

  const handleToggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      const visibleIds = new Set(selectableFilteredUsers.map((user) => user.id));
      setSelectedUserIds((prev) => prev.filter((id) => !visibleIds.has(id)));
      return;
    }
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      selectableFilteredUsers.forEach((user) => next.add(user.id));
      return Array.from(next);
    });
  };

  const pendingRemoveUsers = useMemo(
    () => users.filter((user) => pendingRemoveIds.includes(user.id)),
    [users, pendingRemoveIds]
  );
  const pendingRemoveIncludesInstalled = pendingRemoveUsers.some((user) => (
    Boolean(user.extensionInstalled || user.extensionInstalledAt || user.extensionLastSeenAt || user.trafficLiteLastActiveAt)
  ));

  const handleBulkRemove = async (userIds: string[], reason: string) => {
    if (viewerIsSuperAdmin || userIds.length === 0) return;
    try {
      setRemovingUsers(true);
      setError(null);
      const token = getEffectiveToken();
      if (!token) throw new Error('Not authenticated.');
      const baseUrl = API_BASE_URL_WITH_API.endsWith('/api')
        ? API_BASE_URL_WITH_API
        : `${API_BASE_URL_WITH_API}/api`;
      const response = await fetch(`${baseUrl}/admin/users/bulk-remove`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userIds, reason })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Failed to remove users.');
      const removedCount = Number(data.deletedCount || 0) + Number(data.archivedCount || 0);
      setSelectedUserIds([]);
      setPendingRemoveIds([]);
      setRemoveDialogOpen(false);
      lastUsersFetchAtRef.current = 0;
      await fetchUsers(true);
      toast({
        title: `${removedCount || userIds.length} user${(removedCount || userIds.length) === 1 ? '' : 's'} removed from the active roster.`
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to remove users.');
    } finally {
      setRemovingUsers(false);
    }
  };

  const openWorkspaceModal = (nextMode: 'connect' | 'manage' | 'setup' = 'connect') => {
    setWorkspacePrivilegeFailure(false);
    setWorkspaceModalMode(nextMode);
    setWorkspaceModalOpen(true);
  };

  const statusFilterControl = (
    <div style={{ position: 'relative', minWidth: '220px' }}>
      <button
        type="button"
        aria-label="Filter users by status"
        aria-expanded={statusFilterOpen}
        onClick={() => {
          setStatusFilterOpen((open) => !open);
          setCsvMenuOpen(false);
        }}
        style={{
          width: '100%',
          minWidth: '220px',
          padding: '8px 12px',
          border: '1px solid #d1d5db',
          borderRadius: '8px',
          fontSize: '14px',
          backgroundColor: 'white',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          cursor: 'pointer',
          textAlign: 'left'
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
          <StatusFilterIcon kind={statusFilter} />
          {STATUS_FILTER_OPTIONS.find((option) => option.value === statusFilter)?.label || 'All Users'}
        </span>
        <span
          aria-hidden="true"
          style={{
            width: 0,
            height: 0,
            borderLeft: '4px solid transparent',
            borderRight: '4px solid transparent',
            borderTop: '5px solid #4b5563',
            flex: '0 0 auto'
          }}
        />
      </button>
      {statusFilterOpen && (
        <div
          role="listbox"
          style={{
            position: 'absolute',
            top: '110%',
            left: 0,
            zIndex: 30,
            minWidth: '100%',
            backgroundColor: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '4px',
            boxShadow: '0 8px 20px rgba(15, 23, 42, 0.12)'
          }}
        >
          {STATUS_FILTER_OPTIONS.map((option) => {
            const hovered = statusFilterHover === option.value;
            const selected = statusFilter === option.value;
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={selected}
                onMouseEnter={() => setStatusFilterHover(option.value)}
                onMouseLeave={() => setStatusFilterHover(null)}
                onClick={() => {
                  setStatusFilter(option.value);
                  setSelectedUserIds([]);
                  setStatusFilterOpen(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  width: '100%',
                  textAlign: 'left',
                  padding: '8px 10px',
                  border: 'none',
                  borderRadius: '6px',
                  background: hovered ? '#f3f4f6' : 'transparent',
                  fontSize: '13px',
                  cursor: 'pointer',
                  fontWeight: selected ? 700 : 500
                }}
              >
                <StatusFilterIcon kind={option.value} />
                <span>{option.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  const handleSignOut = () => {
    if (!window.confirm('Are you sure you want to sign out?')) {
      return;
    }

    clearAllAuthStorage();

    const runtime = getChromeRuntime();
    if (runtime) {
      const message = { action: 'CLEAR_ALL_DATA' };
      const extensionId = getExtensionId();
      try {
        if (extensionId) {
          runtime.sendMessage(extensionId, message, () => {});
        } else {
          runtime.sendMessage(message, () => {});
        }
      } catch (error) {
        console.error('Sign-out storage clear failed:', error);
      }
    }

    window.location.href = `${window.location.origin}${window.location.pathname}#/signout-confirmation`;
  };

  const sidebarItems: SideMenuItem[] = [
    ...(viewerIsSuperAdmin
      ? [
          { key: 'users', label: 'Users', onClick: () => navigate('/admin/users'), isActive: true, icon: MENU_ICON_PATHS.users },
          { key: 'domains', label: 'Domains', onClick: () => navigate('/admin/domains'), icon: MENU_ICON_PATHS.domains },
          { key: 'dashboard', label: 'Data Dashboard', onClick: () => navigate('/admin'), icon: MENU_ICON_PATHS.dataDashboard },
          { key: 'policy', label: 'Policy Templates', onClick: () => navigate('/admin/settings'), icon: MENU_ICON_PATHS.policyDetails },
          { key: 'aiAssist', label: 'GPS', onClick: () => navigate('/admin/ai-assist'), icon: MENU_ICON_PATHS.aiAssist }
        ]
      : [
          { key: 'policy', label: 'Policy Details', onClick: () => navigate('/admin/settings'), icon: MENU_ICON_PATHS.policyDetails },
          { key: 'users', label: 'Users', onClick: () => navigate('/admin/users'), isActive: true, icon: MENU_ICON_PATHS.users },
          { key: 'groups', label: 'Groups', onClick: () => navigate('/admin/groups'), icon: MENU_ICON_PATHS.groups },
          { key: 'dashboard', label: 'Data Dashboard', onClick: () => navigate('/admin'), icon: MENU_ICON_PATHS.dataDashboard }
        ]),
    ...(viewerIsSuperAdmin
      ? [
          { key: 'feedback', label: 'Feedback', onClick: () => navigate('/admin/feedback'), icon: MENU_ICON_PATHS.feedback },
          { key: 'signout', label: 'Sign Out', onClick: handleSignOut, isDanger: true, icon: MENU_ICON_PATHS.signOut }
        ]
      : [])
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div
        className={usePersistentSidebar ? 'max-w-[1880px] mx-auto flex gap-6 items-start' : 'max-w-[1500px] mx-auto'}
      >
        {usePersistentSidebar && <SideMenuCard title={viewerIsSuperAdmin ? 'Super Admin Menu' : 'Admin Menu'} items={sidebarItems} />}
        <div className={`${usePersistentSidebar ? 'flex-1 min-w-0' : ''} bg-white rounded-lg shadow-md p-8`} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <AdminHeader
          title={viewerIsSuperAdmin ? 'Traffic Lite Super Admin' : 'Traffic Lite Admin'}
          subtitle="Users"
          helperText={
            viewerIsSuperAdmin
              ? 'Use the table below to sort users, change roles, and manage GPS access.'
              : 'Use this page to sort users, assign groups, and manage staff access.'
          }
          menuOpen={menuOpen}
          setMenuOpen={setMenuOpen}
        />
        <ImpersonationBanner />

        {error && (
          <div
            className="mb-6 p-4 rounded-lg"
            style={{
              backgroundColor: '#fee',
              border: '1px solid #fcc',
              color: '#c00'
            }}
          >
            {error}
          </div>
        )}
        {pageSavedMessage && (
          <div
            className="mb-6 p-4 rounded-lg"
            style={{
              backgroundColor: '#efe',
              border: '1px solid #8f8',
              color: '#0a6'
            }}
          >
            {pageSavedMessage}
          </div>
        )}
        {importSummary && (
          <div className="mb-6 p-4 rounded-lg" style={{ backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e3a8a' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
              <div>
                Import complete: {importSummary.createdCount} created, {importSummary.updatedCount} updated, {importSummary.failedCount} failed.
                {importSummary.failedCount > 0 && (
                  <div style={{ marginTop: '6px', color: '#b91c1c', fontSize: '13px' }}>
                    {importSummary.failed.slice(0, 5).map((item) => (
                      <div key={`${item.row}-${item.email}`}>
                        Row {item.row} ({item.email || 'no email'}): {item.error}
                      </div>
                    ))}
                    {importSummary.failed.length > 5 && <div>...and {importSummary.failed.length - 5} more row errors.</div>}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => setImportSummary(null)}
                style={{
                  border: '1px solid #93c5fd',
                  backgroundColor: 'white',
                  color: '#1d4ed8',
                  borderRadius: '6px',
                  padding: '6px 10px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Dismiss
              </button>
            </div>
          </div>
        )}
        {showAddUserModal && (
          <div
            role="dialog"
            aria-modal="true"
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(17, 24, 39, 0.45)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 80,
              padding: '20px'
            }}
          >
            <div
              style={{
                width: '100%',
                maxWidth: '520px',
                borderRadius: '10px',
                border: '1px solid #e5e7eb',
                backgroundColor: '#fff',
                boxShadow: '0 12px 30px rgba(0,0,0,0.18)',
                padding: '20px'
              }}
            >
              <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#111827', marginBottom: '14px' }}>Add User</h3>
              {addUserModalError && (
                <div
                  style={{
                    marginBottom: '12px',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '1px solid #fecaca',
                    backgroundColor: '#fef2f2',
                    color: '#b91c1c',
                    fontSize: '13px'
                  }}
                >
                  {addUserModalError}
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', color: '#374151', marginBottom: '6px' }}>Last Name</label>
                  <input
                    type="text"
                    value={addUserForm.lastName}
                    onChange={(e) => setAddUserForm((prev) => ({ ...prev, lastName: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      borderRadius: '6px',
                      border: '1px solid #d1d5db',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', color: '#374151', marginBottom: '6px' }}>First Name</label>
                  <input
                    type="text"
                    value={addUserForm.firstName}
                    onChange={(e) => setAddUserForm((prev) => ({ ...prev, firstName: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      borderRadius: '6px',
                      border: '1px solid #d1d5db',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '13px', color: '#374151', marginBottom: '6px' }}>
                  Email <span style={{ color: '#b91c1c' }}>*</span>
                </label>
                <input
                  type="email"
                  value={addUserForm.email}
                  onChange={(e) => setAddUserForm((prev) => ({ ...prev, email: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '14px'
                  }}
                />
              </div>
              {viewerIsSuperAdmin ? (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '13px', color: '#374151', marginBottom: '6px' }}>
                    Role <span style={{ color: '#b91c1c' }}>*</span>
                  </label>
                  <select
                    value={addUserForm.role}
                    onChange={(e) => setAddUserForm((prev) => ({ ...prev, role: e.target.value as 'teacher' | 'admin' }))}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      borderRadius: '6px',
                      border: '1px solid #d1d5db',
                      fontSize: '14px'
                    }}
                  >
                    <option value="teacher">Teacher</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              ) : (
                GROUPS_FEATURE_ENABLED && (
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontSize: '13px', color: '#374151', marginBottom: '6px' }}>Group</label>
                    <select
                      value={addUserForm.groupId}
                      onChange={(e) => setAddUserForm((prev) => ({ ...prev, groupId: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        borderRadius: '6px',
                        border: '1px solid #d1d5db',
                        fontSize: '14px'
                      }}
                    >
                      <option value="">No Group</option>
                      {sortedGroups.map((group) => (
                        <option key={group.id} value={group.id}>
                          {group.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button
                  type="button"
                  onClick={handleCloseAddUserModal}
                  disabled={addingUser}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '8px',
                    border: '1px solid #d1d5db',
                    backgroundColor: 'white',
                    color: '#374151',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: addingUser ? 'not-allowed' : 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void handleAddUserSubmit()}
                  disabled={addingUser}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: '#4285f4',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: 700,
                    cursor: addingUser ? 'not-allowed' : 'pointer'
                  }}
                >
                  {addingUser ? 'Adding...' : 'Add User'}
                </button>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-16 text-gray-500">Loading users...</div>
        ) : (
          <div>
            <div className={viewerIsSuperAdmin ? 'mb-4' : 'mb-0'}>
              {viewerIsSuperAdmin ? (
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <p className="text-gray-600">
                    {sortedUsers.length} user{sortedUsers.length !== 1 ? 's' : ''} across all domains
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <div>
                      <select
                        value={selectedDomain}
                        onChange={(e) => setSelectedDomain(e.target.value)}
                        style={{
                          width: '100%',
                          minWidth: '220px',
                          padding: '8px 12px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '14px',
                          backgroundColor: 'white'
                        }}
                      >
                        <option value="">All domains</option>
                        {domainOptions.map((domain) => (
                          <option key={domain} value={domain}>
                            {domain}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div
                      style={{
                        position: 'relative',
                        minWidth: '320px'
                      }}
                    >
                      <span
                        aria-hidden="true"
                        style={{
                          position: 'absolute',
                          left: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#6b7280',
                          fontSize: '14px'
                        }}
                      >
                        🔍
                      </span>
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search"
                        style={{
                          width: '100%',
                          padding: '8px 12px 8px 34px',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          fontSize: '14px',
                          backgroundColor: 'white'
                        }}
                      />
                    </div>
                    {statusFilterControl}
                    <button
                      type="button"
                      onClick={handleOpenAddUserModal}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: 'none',
                        backgroundColor: '#4285f4',
                        color: 'white',
                        fontSize: '14px',
                        fontWeight: 700,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        cursor: 'pointer'
                      }}
                    >
                      + Add User
                    </button>
                    <button
                      type="button"
                      onClick={handleExportCsv}
                      style={{
                        padding: '8px 20px',
                        fontSize: '14px',
                        fontWeight: '600',
                        backgroundColor: '#455a64',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer'
                      }}
                    >
                      Export CSV
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveAll}
                      disabled={!hasChanges || saving}
                      style={{
                        padding: '8px 20px',
                        fontSize: '14px',
                        fontWeight: '600',
                        backgroundColor: hasChanges && !saving ? '#4285f4' : '#9e9e9e',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: hasChanges && !saving ? 'pointer' : 'not-allowed'
                      }}
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {false && GROUPS_FEATURE_ENABLED && (
                    <div
                      style={{
                        marginBottom: '12px',
                        backgroundColor: '#f9fafb',
                        border: '1px solid #e5e7eb',
                        borderRadius: '10px',
                        width: '100%',
                        maxWidth: '900px',
                        overflow: 'hidden'
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '10px',
                          height: '48px',
                          minHeight: '48px',
                          maxHeight: '48px',
                          backgroundColor: '#f3f4f6',
                          borderRadius: '10px 10px 0 0',
                          padding: '0 12px',
                          position: 'sticky',
                          top: 0,
                          zIndex: 1
                        }}
                      >
                        <h3
                          style={{
                            margin: 0,
                            fontFamily: 'Raleway, sans-serif',
                            fontSize: '16px',
                            fontWeight: 800,
                            color: '#111827',
                            lineHeight: 1.1
                          }}
                        >
                            Groups ({groups.length})
                        </h3>
                        <button
                          type="button"
                          onClick={() => {
                            setGroupsInlineError(null);
                            setGroupsPanelExpanded((prev) => !prev);
                          }}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            whiteSpace: 'nowrap',
                            padding: '6px 12px',
                            borderRadius: '8px',
                            border: '1px solid #d1d5db',
                            backgroundColor: '#ffffff',
                            fontSize: '14px',
                            fontWeight: 600,
                            color: '#374151',
                            cursor: 'pointer'
                          }}
                        >
                          <span>{groupsPanelExpanded ? 'Collapse' : 'Manage'}</span>
                          <span aria-hidden="true" style={{ display: 'inline-flex', alignItems: 'center' }}>
                            <img
                              src={groupsPanelExpanded ? MENU_ICON_PATHS.upArrow : MENU_ICON_PATHS.downArrow}
                              alt=""
                              style={{ width: '11px', height: '11px', objectFit: 'contain' }}
                            />
                          </span>
                        </button>
                      </div>

                      {!groupsPanelExpanded && sortedGroups.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', padding: '12px' }}>
                          {sortedGroups.map((group) => (
                            <button
                              type="button"
                              key={group.id}
                              onClick={() => handlePrioritizeGroup(group.id)}
                              disabled={(groupAssignmentCounts[group.id] || 0) === 0}
                              style={{
                                padding: '4px 10px',
                                borderRadius: '9999px',
                                border: `1px solid ${transientChipHighlight === group.id ? '#c5cae9' : '#111827'}`,
                                backgroundColor: transientChipHighlight === group.id ? '#e8eaf6' : '#ffffff',
                                fontSize: '14px',
                                color: transientChipHighlight === group.id ? '#3949ab' : '#111827',
                                lineHeight: 1.3,
                                cursor: (groupAssignmentCounts[group.id] || 0) > 0 ? 'pointer' : 'not-allowed',
                                opacity: (groupAssignmentCounts[group.id] || 0) > 0 ? 1 : 0.55
                              }}
                            >
                              {group.name}
                            </button>
                          ))}
                          <button
                            type="button"
                            onClick={() => {
                              if (ungroupedUserCount === 0) return;
                              handlePrioritizeGroup(UNGROUPED_PRIORITY_KEY);
                            }}
                            disabled={ungroupedUserCount === 0}
                            style={{
                              padding: '4px 10px',
                              borderRadius: '9999px',
                              border: `1px solid ${transientChipHighlight === UNGROUPED_PRIORITY_KEY ? '#c5cae9' : '#111827'}`,
                              backgroundColor: transientChipHighlight === UNGROUPED_PRIORITY_KEY ? '#e8eaf6' : '#ffffff',
                              fontSize: '14px',
                              color: transientChipHighlight === UNGROUPED_PRIORITY_KEY ? '#3949ab' : '#111827',
                              lineHeight: 1.3,
                              cursor: ungroupedUserCount > 0 ? 'pointer' : 'not-allowed',
                              opacity: ungroupedUserCount > 0 ? 1 : 0.55
                            }}
                          >
                            {ungroupedUserCount} No Group
                          </button>
                        </div>
                      )}

                      {groupsPanelExpanded && (
                        <div style={{ display: 'grid', gap: '8px', padding: '12px' }}>
                          {sortedGroups.map((group) => (
                            <div
                              key={group.id}
                              className="groups-panel-row"
                              style={{
                                padding: '10px 12px',
                                borderRadius: '10px',
                                backgroundColor: '#ffffff',
                                border: '1px solid #e5e7eb'
                              }}
                            >
                              {deletingGroupId === group.id ? (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                                  <span style={{ fontSize: '13px', color: '#374151' }}>
                                    Delete this group? Users will become &quot;No Group.&quot;
                                  </span>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <button
                                      type="button"
                                      disabled={groupsSaving}
                                      onClick={() => void handleDeleteGroupInline(group.id)}
                                      style={{
                                        padding: '5px 10px',
                                        borderRadius: '6px',
                                        border: 'none',
                                        backgroundColor: '#dc2626',
                                        color: 'white',
                                        fontSize: '12px',
                                        fontWeight: 700,
                                        cursor: groupsSaving ? 'not-allowed' : 'pointer',
                                        opacity: groupsSaving ? 0.7 : 1
                                      }}
                                    >
                                      Delete
                                    </button>
                                    <button
                                      type="button"
                                      disabled={groupsSaving}
                                      onClick={() => setDeletingGroupId('')}
                                      style={{
                                        padding: '5px 10px',
                                        borderRadius: '6px',
                                        border: '1px solid #d1d5db',
                                        backgroundColor: 'white',
                                        color: '#374151',
                                        fontSize: '12px',
                                        fontWeight: 700,
                                        cursor: groupsSaving ? 'not-allowed' : 'pointer'
                                      }}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                    {editingGroupId === group.id ? (
                                      <>
                                        <input
                                          value={editingGroupNameInput}
                                          onChange={(e) => setEditingGroupNameInput(e.target.value)}
                                          placeholder="Group name"
                                          style={{
                                            width: '220px',
                                            maxWidth: '100%',
                                            padding: '7px 11px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '6px',
                                            fontSize: '14px'
                                          }}
                                        />
                                        <button
                                          type="button"
                                          disabled={groupsSaving}
                                          onClick={() => void handleSaveRenameGroup(group.id)}
                                          style={{
                                            width: '28px',
                                            height: '28px',
                                            borderRadius: '9999px',
                                            border: '1px solid #16a34a',
                                            backgroundColor: '#ecfdf3',
                                            color: '#15803d',
                                            fontSize: '15px',
                                            fontWeight: 700,
                                            cursor: groupsSaving ? 'not-allowed' : 'pointer'
                                          }}
                                        >
                                          ✓
                                        </button>
                                        <button
                                          type="button"
                                          disabled={groupsSaving}
                                          onClick={() => {
                                            setEditingGroupId('');
                                            setEditingGroupNameInput('');
                                          }}
                                          style={{
                                            width: '28px',
                                            height: '28px',
                                            borderRadius: '9999px',
                                            border: '1px solid #d1d5db',
                                            backgroundColor: 'white',
                                            color: '#6b7280',
                                            fontSize: '16px',
                                            fontWeight: 700,
                                            cursor: groupsSaving ? 'not-allowed' : 'pointer'
                                          }}
                                        >
                                          ×
                                        </button>
                                      </>
                                    ) : (
                                      <>
                                        <span style={{ fontSize: '16px', fontWeight: 700, color: '#111827' }}>{group.name}</span>
                                        <button
                                          type="button"
                                          onClick={() => handlePrioritizeGroup(group.id)}
                                          style={{
                                            border: 'none',
                                            background: 'transparent',
                                            padding: 0,
                                            fontSize: '14px',
                                            color: '#2563eb',
                                            textDecoration: 'underline',
                                            cursor: 'pointer'
                                          }}
                                        >
                                          {groupAssignmentCounts[group.id] || 0} user{(groupAssignmentCounts[group.id] || 0) === 1 ? '' : 's'}
                                        </button>
                                      </>
                                    )}
                                  </div>

                                  {editingGroupId !== group.id && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      <button
                                        type="button"
                                        title="Rename group"
                                        aria-label="Rename group"
                                        disabled={groupsSaving}
                                        onClick={() => handleStartRenameGroup(group)}
                                        style={{
                                          width: '28px',
                                          height: '28px',
                                          borderRadius: '9999px',
                                          border: '1px solid #d1d5db',
                                          backgroundColor: '#ffffff',
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          cursor: groupsSaving ? 'not-allowed' : 'pointer'
                                        }}
                                      >
                                        <img src={GROUP_ICON_PATHS.rename} alt="" style={{ width: '16px', height: '16px' }} />
                                      </button>
                                      <button
                                        type="button"
                                        title="Delete group"
                                        aria-label="Delete group"
                                        disabled={groupsSaving}
                                        onClick={() => {
                                          setEditingGroupId('');
                                          setEditingGroupNameInput('');
                                          setDeletingGroupId(group.id);
                                        }}
                                        style={{
                                          width: '28px',
                                          height: '28px',
                                          borderRadius: '9999px',
                                          border: '1px solid #d1d5db',
                                          backgroundColor: '#ffffff',
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          cursor: groupsSaving ? 'not-allowed' : 'pointer'
                                        }}
                                      >
                                        <img src={GROUP_ICON_PATHS.delete} alt="" style={{ width: '16px', height: '16px' }} />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}

                          {creatingGroup ? (
                            <div
                              style={{
                                padding: '8px 10px',
                                borderRadius: '8px',
                                backgroundColor: '#ffffff',
                                border: '1px dashed #cbd5e1',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                flexWrap: 'wrap'
                              }}
                            >
                              <input
                                value={newGroupNameInput}
                                onChange={(e) => setNewGroupNameInput(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    void handleCreateGroupInline();
                                  }
                                }}
                                placeholder="New group name"
                                style={{
                                  minWidth: '220px',
                                  flex: 1,
                                  padding: '7px 11px',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '6px',
                                  fontSize: '14px'
                                }}
                              />
                              <button
                                type="button"
                                disabled={groupsSaving}
                                onClick={() => void handleCreateGroupInline()}
                                style={{
                                  padding: '7px 13px',
                                  borderRadius: '6px',
                                  border: 'none',
                                  backgroundColor: '#4285f4',
                                  color: 'white',
                                  fontSize: '13px',
                                  fontWeight: 700,
                                  cursor: groupsSaving ? 'not-allowed' : 'pointer',
                                  opacity: groupsSaving ? 0.7 : 1
                                }}
                              >
                                Create
                              </button>
                              <button
                                type="button"
                                disabled={groupsSaving}
                                onClick={() => {
                                  setCreatingGroup(false);
                                  setNewGroupNameInput('');
                                  setGroupsInlineError(null);
                                }}
                                style={{
                                  padding: '7px 13px',
                                  borderRadius: '6px',
                                  border: '1px solid #d1d5db',
                                  backgroundColor: 'white',
                                  color: '#374151',
                                  fontSize: '13px',
                                  fontWeight: 700,
                                  cursor: groupsSaving ? 'not-allowed' : 'pointer'
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              disabled={groupsSaving}
                              onClick={() => {
                                setGroupsInlineError(null);
                                setDeletingGroupId('');
                                setEditingGroupId('');
                                setCreatingGroup(true);
                                setNewGroupNameInput('');
                              }}
                              style={{
                                width: 'fit-content',
                                padding: '8px 12px',
                                borderRadius: '8px',
                                border: '1px solid transparent',
                                backgroundColor: '#4285f4',
                                color: 'white',
                                fontSize: '14px',
                                fontWeight: 600,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                cursor: groupsSaving ? 'not-allowed' : 'pointer',
                                opacity: groupsSaving ? 0.7 : 1
                              }}
                            >
                              <img src={GROUP_ICON_PATHS.addNew} alt="" style={{ width: '14px', height: '14px', filter: 'brightness(0) invert(1)' }} />
                              <span className="new-group-label">New Group</span>
                            </button>
                          )}

                          {groupsInlineError && (
                            <div style={{ fontSize: '12px', color: '#b91c1c' }}>
                              {groupsInlineError}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <div
                    style={{
                      marginTop: '10px',
                      marginBottom: '12px'
                    }}
                  >
                    <div
                      style={{
                        fontFamily: 'Raleway, sans-serif',
                        fontSize: '16px',
                        fontWeight: 800,
                        color: '#111827',
                        lineHeight: 1.1,
                        marginBottom: '10px'
                      }}
                    >
                      Users ({sortedUsers.length})
                    </div>
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        <div
                          style={{
                            position: 'relative',
                            minWidth: '320px'
                          }}
                        >
                          <span
                            aria-hidden="true"
                            style={{
                              position: 'absolute',
                              left: '10px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              color: '#6b7280',
                              fontSize: '14px'
                            }}
                          >
                            🔍
                          </span>
                          <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search"
                            style={{
                              width: '100%',
                              padding: '8px 12px 8px 34px',
                              border: '1px solid #d1d5db',
                              borderRadius: '8px',
                              fontSize: '14px',
                              backgroundColor: 'white'
                            }}
                          />
                        </div>
                        {statusFilterControl}
                        <button
                          type="button"
                          onClick={handleOpenAddUserModal}
                          style={{
                            padding: '8px 12px',
                            borderRadius: '8px',
                            border: 'none',
                            backgroundColor: '#4285f4',
                            color: 'white',
                            fontSize: '14px',
                            fontWeight: 700,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            cursor: 'pointer'
                          }}
                        >
                          + Add User
                        </button>
                        <div style={{ position: 'relative' }}>
                          <button
                            type="button"
                            onClick={() => {
                              setCsvMenuOpen((open) => !open);
                              setStatusFilterOpen(false);
                            }}
                            disabled={importingUsers}
                            style={{
                              padding: '8px 12px',
                              borderRadius: '8px',
                              border: 'none',
                              backgroundColor: importingUsers ? '#9ca3af' : '#4285f4',
                              color: 'white',
                              fontSize: '14px',
                              fontWeight: 700,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              cursor: importingUsers ? 'not-allowed' : 'pointer'
                            }}
                          >
                            <img src={MENU_ICON_PATHS.upload} alt="" style={{ width: '14px', height: '14px', filter: 'brightness(0) invert(1)' }} />
                            {importingUsers ? 'Importing...' : 'Import CSV'}
                            <span
                              aria-hidden="true"
                              style={{
                                width: 0,
                                height: 0,
                                borderLeft: '4px solid transparent',
                                borderRight: '4px solid transparent',
                                borderTop: '5px solid currentColor',
                                marginLeft: '2px'
                              }}
                            />
                          </button>
                          {csvMenuOpen && (
                            <div
                              style={{
                                position: 'absolute',
                                top: '110%',
                                left: 0,
                                zIndex: 20,
                                minWidth: '240px',
                                backgroundColor: '#fff',
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                padding: '6px',
                                boxShadow: '0 8px 20px rgba(15, 23, 42, 0.12)'
                              }}
                            >
                              <button
                                type="button"
                                onMouseEnter={() => setCsvMenuHover('upload')}
                                onMouseLeave={() => setCsvMenuHover(null)}
                                onClick={() => {
                                  setCsvMenuOpen(false);
                                  setCsvMenuHover(null);
                                  handleOpenImportUsers();
                                }}
                                style={{
                                  display: 'block',
                                  width: '100%',
                                  textAlign: 'left',
                                  padding: '8px 10px',
                                  border: 'none',
                                  borderRadius: '6px',
                                  background: csvMenuHover === 'upload' ? '#f3f4f6' : 'transparent',
                                  fontSize: '13px',
                                  cursor: 'pointer'
                                }}
                              >
                                Upload CSV
                                <div style={{ fontSize: '12px', color: '#6b7280' }}>Add multiple users from a spreadsheet.</div>
                              </button>
                              <button
                                type="button"
                                onMouseEnter={() => setCsvMenuHover('template')}
                                onMouseLeave={() => setCsvMenuHover(null)}
                                onClick={() => {
                                  setCsvMenuOpen(false);
                                  setCsvMenuHover(null);
                                  handleDownloadCsvTemplate();
                                }}
                                style={{
                                  display: 'block',
                                  width: '100%',
                                  textAlign: 'left',
                                  padding: '8px 10px',
                                  border: 'none',
                                  borderRadius: '6px',
                                  background: csvMenuHover === 'template' ? '#f3f4f6' : 'transparent',
                                  fontSize: '13px',
                                  cursor: 'pointer'
                                }}
                              >
                                Download CSV Template
                                <div style={{ fontSize: '12px', color: '#6b7280' }}>Download the Traffic Lite template.</div>
                              </button>
                            </div>
                          )}
                        </div>
                        {workspaceConnection?.status === 'connected' && workspaceConnection?.lastSyncAt ? (
                          <>
                            <span
                              title={`Last synced ${new Date(workspaceConnection.lastSyncAt).toLocaleString(undefined, {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit'
                              })}`}
                              style={{
                                padding: '8px 12px',
                                borderRadius: '8px',
                                border: '1px solid #a5d6a7',
                                backgroundColor: '#e8f5e9',
                                color: '#2e7d32',
                                fontSize: '14px',
                                fontWeight: 700,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                cursor: 'default',
                                userSelect: 'none'
                              }}
                            >
                              Google Workspace Synced ✓
                            </span>
                            <button
                              type="button"
                              onClick={() => openWorkspaceModal('manage')}
                              style={{
                                border: 'none',
                                background: 'transparent',
                                color: '#2563eb',
                                fontSize: '14px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                padding: 0
                              }}
                            >
                              Manage Sync
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            onClick={() => openWorkspaceModal('connect')}
                            style={{
                              padding: '8px 12px',
                              borderRadius: '8px',
                              border: 'none',
                              backgroundColor: '#4285f4',
                              color: 'white',
                              fontSize: '14px',
                              fontWeight: 700,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              cursor: 'pointer'
                            }}
                          >
                            Sync with Google Workspace
                          </button>
                        )}
                        <input
                          ref={csvFileInputRef}
                          type="file"
                          accept=".csv,text/csv"
                          onChange={(e) => void handleImportUsersFile(e)}
                          style={{ display: 'none' }}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => void handleSaveGroupChanges()}
                        disabled={groupsSaving || !hasGroupChanges}
                        style={{
                          padding: '8px 14px',
                          fontSize: '14px',
                          fontWeight: '600',
                          backgroundColor: hasGroupChanges && !groupsSaving ? '#4285f4' : '#9e9e9e',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: groupsSaving || !hasGroupChanges ? 'not-allowed' : 'pointer',
                          opacity: groupsSaving || !hasGroupChanges ? 0.7 : 1
                        }}
                      >
                        {groupsSaving ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
            {!viewerIsSuperAdmin && selectedVisibleCount > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '0 0 10px' }}>
                <span style={{ fontSize: '14px', fontWeight: 600 }}>{selectedVisibleCount} selected</span>
                <button
                  type="button"
                  disabled={removingUsers}
                  onClick={() => {
                    setPendingRemoveIds(selectableFilteredUsers.filter((user) => selectedUserIds.includes(user.id)).map((user) => user.id));
                    setRemoveDialogOpen(true);
                  }}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: '#b91c1c',
                    color: '#fff',
                    fontWeight: 700,
                    cursor: removingUsers ? 'not-allowed' : 'pointer'
                  }}
                >
                  {removingUsers ? 'Removing...' : `Remove ${selectedVisibleCount} User${selectedVisibleCount === 1 ? '' : 's'}`}
                </button>
              </div>
            )}
            <div
              style={{
                overflowX: 'auto'
              }}
            >
              <table
                className="portal-table"
                style={{
                  width: '100%',
                  minWidth: viewerIsSuperAdmin ? '1720px' : '100%',
                  borderCollapse: 'collapse',
                  fontSize: '14px'
                }}
              >
                <thead>
                  <tr
                    style={{
                      backgroundColor: '#212529',
                      borderBottom: '2px solid #212529'
                    }}
                  >
                    {!viewerIsSuperAdmin && (
                      <th
                        style={{
                          padding: '12px',
                          fontWeight: '600',
                          color: 'white',
                          borderRight: '1px solid rgba(255,255,255,0.3)',
                          width: '42px'
                        }}
                      >
                        <input
                          type="checkbox"
                          aria-label="Select all filtered users"
                          checked={allVisibleSelected}
                          onChange={handleToggleSelectAllVisible}
                        />
                      </th>
                    )}
                    <th
                      onClick={() => handleSort('name')}
                      style={{
                        padding: '12px',
                        fontWeight: '600',
                        color: 'white',
                        borderRight: '1px solid rgba(255,255,255,0.3)',
                        cursor: 'pointer',
                        userSelect: 'none',
                        width: !viewerIsSuperAdmin ? '23%' : '190px',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Name (Last, First){renderSortIndicator('name')}
                    </th>
                    <th
                      onClick={() => handleSort('email')}
                      style={{
                        padding: '12px',
                        fontWeight: '600',
                        color: 'white',
                        borderRight: '1px solid rgba(255,255,255,0.3)',
                        cursor: 'pointer',
                        userSelect: 'none',
                        width: !viewerIsSuperAdmin ? '23%' : '185px',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Email{renderSortIndicator('email')}
                    </th>
                    {viewerIsSuperAdmin && (
                      <th
                        onClick={() => handleSort('installDate')}
                        style={{
                          padding: '12px',
                          textAlign: 'left',
                          fontWeight: '600',
                          color: 'white',
                          borderRight: '1px solid rgba(255,255,255,0.3)',
                          cursor: 'pointer',
                          userSelect: 'none',
                          width: '140px'
                        }}
                      >
                        Install Date{renderSortIndicator('installDate')}
                      </th>
                    )}
                    {showVersionColumn && (
                      <th
                        onClick={() => handleSort('version')}
                        style={{
                          padding: '12px',
                          textAlign: 'left',
                          fontWeight: '600',
                          color: 'white',
                          borderRight: '1px solid rgba(255,255,255,0.3)',
                          cursor: 'pointer',
                          userSelect: 'none',
                          width: '90px'
                        }}
                      >
                        Version{renderSortIndicator('version')}
                      </th>
                    )}
                    {viewerIsSuperAdmin && (
                      <th
                        onClick={() => handleSort('lastClick')}
                        style={{
                          padding: '12px',
                          textAlign: 'left',
                          fontWeight: '600',
                          color: 'white',
                          borderRight: '1px solid rgba(255,255,255,0.3)',
                          cursor: 'pointer',
                          userSelect: 'none',
                          width: '140px'
                        }}
                      >
                        Last Active{renderSortIndicator('lastClick')}
                      </th>
                    )}
                    {viewerIsSuperAdmin && (
                      <th
                        onClick={() => handleSort('status')}
                        style={{
                          padding: '12px',
                          textAlign: 'center',
                          fontWeight: '600',
                          color: 'white',
                          borderRight: '1px solid rgba(255,255,255,0.3)',
                          cursor: 'pointer',
                          userSelect: 'none',
                          width: '88px'
                        }}
                      >
                        Status{renderSortIndicator('status')}
                      </th>
                    )}
                    {viewerIsSuperAdmin && (
                      <th
                        onClick={() => handleSort('weeklyClicks')}
                        style={{
                          padding: '12px',
                          textAlign: 'left',
                          fontWeight: '600',
                          color: 'white',
                          borderRight: '1px solid rgba(255,255,255,0.3)',
                          cursor: 'pointer',
                          userSelect: 'none',
                          width: '92px'
                        }}
                      >
                        Weekly Clicks{renderSortIndicator('weeklyClicks')}
                      </th>
                    )}
                    {viewerIsSuperAdmin && (
                      <th
                        onClick={() => handleSort('coTeacherAccessLevel')}
                        style={{
                          padding: '12px',
                          textAlign: 'left',
                          fontWeight: '600',
                          color: 'white',
                          borderRight: '1px solid rgba(255,255,255,0.3)',
                          cursor: 'pointer',
                          userSelect: 'none',
                          width: '92px'
                        }}
                      >
                        GPS{renderSortIndicator('coTeacherAccessLevel')}
                      </th>
                    )}
                    {viewerIsSuperAdmin && (
                      <th
                        onClick={() => handleSort('assistClicks')}
                        style={{
                          padding: '12px',
                          textAlign: 'left',
                          fontWeight: '600',
                          color: 'white',
                          borderRight: '1px solid rgba(255,255,255,0.3)',
                          cursor: 'pointer',
                          userSelect: 'none',
                          width: '92px'
                        }}
                      >
                        GPS Clicks{renderSortIndicator('assistClicks')}
                      </th>
                    )}
                    {viewerIsSuperAdmin && (
                      <th
                        onClick={() => handleSort('assistInserts')}
                        style={{
                          padding: '12px',
                          textAlign: 'left',
                          fontWeight: '600',
                          color: 'white',
                          borderRight: '1px solid rgba(255,255,255,0.3)',
                          cursor: 'pointer',
                          userSelect: 'none',
                          width: '92px'
                        }}
                      >
                        Suggestion Inserts{renderSortIndicator('assistInserts')}
                      </th>
                    )}
                    {viewerIsSuperAdmin && (
                      <th
                        onClick={() => handleSort('gpsTeaserStatus')}
                        style={{
                          padding: '12px',
                          textAlign: 'left',
                          fontWeight: '600',
                          color: 'white',
                          borderRight: '1px solid rgba(255,255,255,0.3)',
                          cursor: 'pointer',
                          userSelect: 'none',
                          width: '92px'
                        }}
                      >
                        GPS Teaser{renderSortIndicator('gpsTeaserStatus')}
                      </th>
                    )}
                    {viewerIsSuperAdmin && (
                      <th
                        onClick={() => handleSort('scale')}
                        style={{
                          padding: '12px',
                          textAlign: 'left',
                          fontWeight: '600',
                          color: 'white',
                          borderRight: '1px solid rgba(255,255,255,0.3)',
                          cursor: 'pointer',
                          userSelect: 'none',
                          width: '92px'
                        }}
                      >
                        Scale{renderSortIndicator('scale')}
                      </th>
                    )}
                    <th
                      onClick={() => handleSort('role')}
                      style={{
                        padding: '12px',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: 'white',
                        borderRight: '1px solid rgba(255,255,255,0.3)',
                        cursor: 'pointer',
                        userSelect: 'none',
                        width: '92px',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Role{renderSortIndicator('role')}
                    </th>
                    {viewerIsSuperAdmin && (
                      <th
                        onClick={() => handleSort('teacherPortal')}
                        style={{
                          padding: '12px',
                          textAlign: 'left',
                          fontWeight: '600',
                          color: 'white',
                          borderRight: '1px solid rgba(255,255,255,0.3)',
                          cursor: 'pointer',
                          userSelect: 'none',
                          width: '92px'
                        }}
                      >
                        Log in as User{renderSortIndicator('teacherPortal')}
                      </th>
                    )}
                    {!viewerIsSuperAdmin && GROUPS_FEATURE_ENABLED && (
                      <th
                        onClick={() => handleSort('group')}
                        style={{
                          padding: '12px',
                          fontWeight: '600',
                          color: 'white',
                          cursor: 'pointer',
                          userSelect: 'none',
                          width: '220px',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Group{renderSortIndicator('group')}
                      </th>
                    )}
                    {!viewerIsSuperAdmin && GROUPS_FEATURE_ENABLED && (
                      <th
                        onClick={() => handleSort('scale')}
                        style={{
                          padding: '12px',
                          fontWeight: '600',
                          color: 'white',
                          cursor: 'pointer',
                          userSelect: 'none',
                          width: '95px',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Scale{renderSortIndicator('scale')}
                      </th>
                    )}
                    {!viewerIsSuperAdmin && (
                      <th
                        onClick={() => handleSort('status')}
                        style={{
                          padding: '12px',
                          fontWeight: '600',
                          color: 'white',
                          cursor: 'pointer',
                          userSelect: 'none',
                          width: '88px',
                          textAlign: 'center',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Status{renderSortIndicator('status')}
                      </th>
                    )}
                    {!viewerIsSuperAdmin && GROUPS_FEATURE_ENABLED && (
                      <th
                        onClick={() => handleSort('viewEditPolicy')}
                        style={{
                          padding: '12px',
                          fontWeight: '600',
                          color: 'white',
                          cursor: 'pointer',
                          userSelect: 'none',
                          width: '210px',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {'View\u00A0&\u00A0Edit\u00A0Policy'}{renderSortIndicator('viewEditPolicy')}
                      </th>
                    )}
                    {!viewerIsSuperAdmin && GROUPS_FEATURE_ENABLED && (
                      <th
                        onClick={() => handleSort('viewPolicy')}
                        style={{
                          padding: '12px',
                          fontWeight: '600',
                          color: 'white',
                          cursor: 'pointer',
                          userSelect: 'none',
                          width: '155px',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {'Log\u00A0in\u00A0as\u00A0User'}{renderSortIndicator('viewPolicy')}
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {sortedUsers.map((user, index) => {
                    const isPreProvisioned = !isExtensionInstalledUser(user);
                    return (
                    <tr
                      key={user.id || user.email}
                      onMouseEnter={() => setHoveredRowId(user.id)}
                      onMouseLeave={() => setHoveredRowId('')}
                      style={{
                        borderBottom: '1px solid #eee',
                        backgroundColor: isPreProvisioned ? '#f3f4f6' : (index % 2 === 0 ? 'white' : '#fafafa'),
                        fontStyle: isPreProvisioned ? 'italic' : 'normal',
                        color: isPreProvisioned ? '#6b7280' : undefined,
                        opacity: isPreProvisioned ? 0.82 : 1
                      }}
                    >
                      {!viewerIsSuperAdmin && (
                        <td style={{ padding: '12px', borderRight: '1px solid #eee', textAlign: 'center', width: '42px' }}>
                          {user.eligibleForBulkRemoval !== false && !user.isAdmin ? (
                            <input
                              type="checkbox"
                              aria-label={`Select ${user.email}`}
                              checked={selectedUserIds.includes(user.id)}
                              onChange={() => {
                                setSelectedUserIds((prev) => (
                                  prev.includes(user.id)
                                    ? prev.filter((id) => id !== user.id)
                                    : [...prev, user.id]
                                ));
                              }}
                            />
                          ) : null}
                        </td>
                      )}
                      <td style={{ padding: '12px', borderRight: '1px solid #eee', width: viewerIsSuperAdmin ? '190px' : undefined }}>
                        {user.name || '-'}
                      </td>
                      <td style={{ padding: '12px', borderRight: '1px solid #eee', width: viewerIsSuperAdmin ? '185px' : undefined }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'space-between' }}>
                          <span>{user.email || '-'}</span>
                          {isPreProvisioned && hoveredRowId === user.id && (
                            <button
                              type="button"
                              onClick={() => void handleDeleteUser(user)}
                              title="Delete user"
                              aria-label="Delete user"
                              className="icon-action-btn"
                            >
                              <span
                                aria-hidden="true"
                                className="icon-action-glyph"
                                style={{
                                  WebkitMaskImage: 'url(/menu-icons/group-delete.png)',
                                  maskImage: 'url(/menu-icons/group-delete.png)'
                                }}
                              />
                            </button>
                          )}
                        </div>
                      </td>
                      {viewerIsSuperAdmin && (
                        <td style={{ padding: '12px', borderRight: '1px solid #eee', width: '140px' }}>
                          {formatInstallDate(user.extensionInstalledAt)}
                        </td>
                      )}
                      {showVersionColumn && (
                        <td style={{ padding: '12px', borderRight: '1px solid #eee', width: '90px' }}>
                          {user.extensionVersion || '-'}
                        </td>
                      )}
                      {viewerIsSuperAdmin && (
                        <td style={{ padding: '12px', borderRight: '1px solid #eee', width: '140px' }}>
                          {formatLastClick(user.lastClickAt || user.trafficLiteLastActiveAt)}
                        </td>
                      )}
                      {viewerIsSuperAdmin && (
                        <td style={{ padding: '12px', borderRight: '1px solid #eee', width: '88px', textAlign: 'center' }}>
                          <UserStatusIcons
                            activityStatus={user.activityStatus}
                            activityStatusTooltip={user.activityStatusTooltip}
                            workspaceWarning={user.workspaceWarning}
                            workspaceWarningTooltip={user.workspaceWarningTooltip}
                          />
                        </td>
                      )}
                      {viewerIsSuperAdmin && (
                        <td style={{ padding: '12px', borderRight: '1px solid #eee', width: '92px' }}>
                          {Math.round(Number(user.weeklyClicks || 0))}
                        </td>
                      )}
                      {viewerIsSuperAdmin && (
                        <td style={{ padding: '12px', borderRight: '1px solid #eee', width: '92px', textAlign: 'center' }}>
                          {user.role === 'super_admin' ? (
                            <span style={{ color: '#6b7280', fontSize: '12px' }}>-</span>
                          ) : (
                            <select
                              value={getEffectiveCoTeacherAccess(user)}
                              onChange={(e) => handleCoTeacherAccessChange(user.id, e.target.value as CoTeacherAccessLevel)}
                              aria-label={`Set GPS access for ${user.email}`}
                              style={{
                                width: '78px',
                                padding: '4px 4px',
                                border: '1px solid',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: 500,
                                ...getGpsBadgeStyles(getEffectiveCoTeacherAccess(user))
                              }}
                            >
                              <option value="off">Off</option>
                              <option value="basic">Basic</option>
                              <option value="premium">Premium</option>
                            </select>
                          )}
                        </td>
                      )}
                      {viewerIsSuperAdmin && (
                        <td style={{ padding: '12px', borderRight: '1px solid #eee', width: '92px' }}>
                          {Number(user.assistClicks || 0)}
                        </td>
                      )}
                      {viewerIsSuperAdmin && (
                        <td style={{ padding: '12px', borderRight: '1px solid #eee', width: '92px' }}>
                          {Number(user.assistInserts || 0)}
                        </td>
                      )}
                      {viewerIsSuperAdmin && (
                        <td style={{ padding: '12px', borderRight: '1px solid #eee', width: '92px' }}>
                          {formatGpsTeaserStatusLabel(user.gpsTeaserStatus)}
                        </td>
                      )}
                      {viewerIsSuperAdmin && (
                        <td style={{ padding: '12px', borderRight: '1px solid #eee', width: '92px' }}>
                          {formatScaleLabel(user.effectiveScale)}
                        </td>
                      )}
                      <td style={{ padding: '12px', borderRight: viewerIsSuperAdmin || (!viewerIsSuperAdmin && GROUPS_FEATURE_ENABLED) ? '1px solid #eee' : undefined, width: viewerIsSuperAdmin ? '92px' : undefined }}>
                        {viewerIsSuperAdmin && user.role !== 'super_admin' ? (
                          <select
                            value={getEffectiveRole(user)}
                            onChange={(e) => handleRoleChange(user.id, e.target.value)}
                            style={{
                              padding: '6px 10px',
                              borderRadius: '6px',
                              border: '1px solid',
                              fontSize: '13px',
                              minWidth: '84px',
                              fontWeight: '500',
                              ...getRoleBadgeStyles(getEffectiveRole(user))
                            }}
                          >
                            <option value="teacher">Teacher</option>
                            {!isGmailUser(user.email) && <option value="admin">Admin</option>}
                          </select>
                        ) : (
                          <span
                            style={{
                              display: 'inline-block',
                              padding: '4px 10px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: '500',
                              border: '1px solid',
                              ...getRoleBadgeStyles(user.role || 'teacher')
                            }}
                          >
                            {user.role || 'teacher'}
                          </span>
                        )}
                      </td>
                      {viewerIsSuperAdmin && (
                        <td style={{ padding: '12px', borderRight: '1px solid #eee', textAlign: 'center', width: '92px' }}>
                          {isPreProvisioned
                            ? null
                            : (String(user.rawRole || user.role || '').toLowerCase() !== 'super_admin'
                              ? renderPortalIconButton({
                                  label: user.isAdmin ? 'View Admin Portal' : 'View Teacher Portal',
                                  onClick: () => void handleStartUserImpersonation(user),
                                  iconPath: MENU_ICON_PATHS.loginAsUser
                                })
                              : null)}
                        </td>
                      )}
                      {!viewerIsSuperAdmin && GROUPS_FEATURE_ENABLED && (
                        <td style={{ padding: '12px', borderRight: '1px solid #eee' }}>
                          {(user.rawRole || user.role) === 'teacher' && !user.isAdmin ? (
                            <select
                              value={getEffectiveGroupId(user)}
                              onChange={(e) => void handleGroupSelectChange(user.id, e.target.value)}
                              disabled={groupsSaving}
                              style={{
                                padding: '8px 12px',
                                borderRadius: '6px',
                                border: '1px solid #bfdbfe',
                                fontSize: '13px',
                                minWidth: '170px',
                                backgroundColor: groupsSaving
                                  ? '#f5f5f5'
                                  : (getEffectiveGroupId(user) ? '#eaf2ff' : 'white'),
                                cursor: groupsSaving ? 'not-allowed' : 'pointer'
                              }}
                            >
                              <option value="">No Group</option>
                              {sortedGroups.map((group) => (
                                <option key={group.id} value={group.id}>
                                  {group.name}
                                </option>
                              ))}
                            </select>
                          ) : null}
                        </td>
                      )}
                      {!viewerIsSuperAdmin && GROUPS_FEATURE_ENABLED && (
                        <td style={{ padding: '12px', borderRight: '1px solid #eee', textAlign: 'center' }}>
                          {(user.rawRole || user.role) === 'teacher' && !user.isAdmin
                            ? formatScaleLabel(user.effectiveScale)
                            : ''}
                        </td>
                      )}
                      {!viewerIsSuperAdmin && (
                        <td style={{ padding: '12px', borderRight: '1px solid #eee', textAlign: 'center' }}>
                          <UserStatusIcons
                            activityStatus={user.activityStatus}
                            activityStatusTooltip={user.activityStatusTooltip}
                            workspaceWarning={user.workspaceWarning}
                            workspaceWarningTooltip={user.workspaceWarningTooltip}
                          />
                        </td>
                      )}
                      {!viewerIsSuperAdmin && GROUPS_FEATURE_ENABLED && (
                        <td style={{ padding: '12px', borderRight: '1px solid #eee', textAlign: 'center' }}>
                          {renderPortalIconButton({
                            label: 'View & Edit Policy',
                            onClick: () => handleOpenViewEditPolicy(user),
                            iconPath: MENU_ICON_PATHS.policyDetails
                          })}
                        </td>
                      )}
                      {!viewerIsSuperAdmin && GROUPS_FEATURE_ENABLED && (
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          {isPreProvisioned
                            ? null
                            : ((user.rawRole || user.role) === 'teacher' && !user.isAdmin
                              ? renderPortalIconButton({
                                  label: 'Log in as User',
                                  onClick: () => void handleStartUserImpersonation(user),
                                  iconPath: MENU_ICON_PATHS.loginAsUser
                                })
                              : null)}
                        </td>
                      )}
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {sortedUsers.length === 0 && !error && (
              <div className="text-center py-12 text-gray-500">
                No users found in your domain. Users appear here after they sign in with Traffic Lite.
              </div>
            )}
          </div>
        )}
        {!viewerIsSuperAdmin && (
          <WorkspaceSyncModal
            open={workspaceModalOpen}
            mode={workspaceModalMode}
            privilegeFailure={workspacePrivilegeFailure}
            onClose={() => {
              setWorkspaceModalOpen(false);
              setWorkspacePrivilegeFailure(false);
            }}
            onChanged={async () => {
              lastUsersFetchAtRef.current = 0;
              await fetchUsers(true);
              await fetchWorkspaceStatus();
            }}
          />
        )}
        <AlertDialog open={removeDialogOpen} onOpenChange={setRemoveDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                Remove {pendingRemoveIds.length} User{pendingRemoveIds.length === 1 ? '' : 's'}?
              </AlertDialogTitle>
              <AlertDialogDescription>
                These users will be removed from the active Traffic Lite roster.
                {pendingRemoveIncludesInstalled
                  ? ' Installed users will be archived so historical Traffic Lite analytics are preserved.'
                  : ''}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={removingUsers}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-red-600 hover:bg-red-700"
                disabled={removingUsers || pendingRemoveIds.length === 0}
                onClick={(event) => {
                  event.preventDefault();
                  void handleBulkRemove(pendingRemoveIds, 'admin_bulk_removal');
                }}
              >
                {removingUsers ? 'Removing…' : `Remove ${pendingRemoveIds.length} User${pendingRemoveIds.length === 1 ? '' : 's'}`}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        {!viewerIsSuperAdmin && <FeedbackFab />}
        </div>
      </div>
    </div>
  );
};

export default AdminUsersPage;
